import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

export interface OrgFilterConfig {
    key: string;
    label: string;
    icon: string;
    count?: number;
}

@Component({
    selector: 'shared-org-filter-bar',
    imports: [MatButtonModule, MatIconModule],
    template: `
        <div class="filter-bar" [style]="activeStyle">
            <!-- Mobile-only collapse toggle. Hidden on desktop via CSS; on phones it
                 stands in front of the chips so the (often long) filter row doesn't
                 eat the whole screen. The active count keeps the collapsed bar honest. -->
            <button
                type="button"
                class="filters-toggle"
                mat-stroked-button
                [class.active]="activeCount > 0"
                [attr.aria-expanded]="filtersExpanded"
                (click)="toggleFilters()"
            >
                <mat-icon class="filter-icon">tune</mat-icon>
                Filters{{ activeCount > 0 ? ' (' + activeCount + ')' : '' }}
                <mat-icon class="chevron" [class.open]="filtersExpanded">expand_more</mat-icon>
            </button>

            <div class="filter-chips filter-row" [class.collapsed]="!filtersExpanded">
                <button mat-stroked-button [class.active]="activeFilters.size === 0" (click)="clearAll()">
                    All{{ totalCount !== null && totalCount !== undefined ? ' (' + totalCount + ')' : '' }}
                </button>
                @for (filter of filters; track filter.key) {
                    <button
                        mat-stroked-button
                        [class.active]="activeFilters.has(filter.key)"
                        (click)="toggle(filter.key)"
                    >
                        <mat-icon class="filter-icon">{{ filter.icon }}</mat-icon>
                        {{ filter.label
                        }}{{ filter.count !== null && filter.count !== undefined ? ' (' + filter.count + ')' : '' }}
                    </button>
                }
                @if (rightFilters.length > 0) {
                    <span class="filter-spacer"></span>
                    @for (filter of rightFilters; track filter.key) {
                        <button
                            mat-stroked-button
                            [class.active]="activeFilters.has(filter.key)"
                            (click)="toggle(filter.key)"
                        >
                            <mat-icon class="filter-icon">{{ filter.icon }}</mat-icon>
                            {{ filter.label
                            }}{{ filter.count !== null && filter.count !== undefined ? ' (' + filter.count + ')' : '' }}
                        </button>
                    }
                }
            </div>
        </div>
    `,
    styles: [
        `
            /* The collapse toggle is desktop-hidden; chips show inline as before. */
            .filters-toggle {
                display: none;
            }

            .chevron {
                transition: transform 0.2s ease;
                margin-left: 2px;
            }
            .chevron.open {
                transform: rotate(180deg);
            }

            .filter-row {
                display: flex;
                gap: 8px;
                margin-bottom: 16px;
                align-items: center;
                flex-wrap: wrap;

                button {
                    font-size: 13px;
                    border-color: rgba(0, 0, 0, 0.15);
                    color: rgba(0, 0, 0, 0.5);

                    &.active {
                        border-color: var(--filter-active-color, #1093ad);
                        color: var(--filter-active-color, #1093ad);
                        background: color-mix(in srgb, var(--filter-active-color, #1093ad) 10%, transparent);
                    }
                }

                .filter-icon {
                    font-size: 16px;
                    width: 16px;
                    height: 16px;
                    margin-right: 4px;
                    vertical-align: middle;
                }

                .filter-spacer {
                    flex: 1;
                }
            }

            :host-context(.dark-theme) .filter-row button {
                border-color: rgba(255, 255, 255, 0.15);
                color: rgba(255, 255, 255, 0.6);

                &.active {
                    border-color: var(--filter-active-dark-color, var(--filter-active-color, #26c6da));
                    color: var(--filter-active-dark-color, var(--filter-active-color, #4dd0e1));
                    background: color-mix(
                        in srgb,
                        var(--filter-active-dark-color, var(--filter-active-color, #26c6da)) 15%,
                        transparent
                    );
                }
            }

            /* ── Phones ────────────────────────────────────────────────────────────
           Show the collapse toggle, hide the chips until expanded. When open the
           chips lay out as a vertical, scrollable list so long filter sets never
           crowd out the content below. */
            @media (max-width: 767.98px) {
                .filters-toggle {
                    display: inline-flex;
                    align-items: center;
                    width: 100%;
                    justify-content: flex-start;
                    margin-bottom: 12px;
                }
                .filters-toggle .chevron {
                    margin-left: auto;
                }

                .filter-chips.collapsed {
                    display: none;
                }

                .filter-chips {
                    /* Stack chips full-width so labels never truncate / squash. */
                    flex-direction: column;
                    align-items: stretch;
                    flex-wrap: nowrap;
                    gap: 8px;
                    margin-bottom: 16px;
                    max-height: 50vh;
                    overflow-y: auto;
                    -webkit-overflow-scrolling: touch;

                    button {
                        width: 100%;
                        justify-content: flex-start;
                    }

                    .filter-spacer {
                        display: none;
                    }
                }
            }
        `,
    ],
})
export class OrgFilterBarComponent {
    /** Main filter pills (left side). */
    @Input() filters: OrgFilterConfig[] = [];

    /** Optional right-aligned filter pills (e.g. "By Position" grouping toggle). */
    @Input() rightFilters: OrgFilterConfig[] = [];

    /** CSS color for active pill border + text. Defaults to cyan. */
    @Input() activeColor = '#1093ad';

    /** Dark-theme active color variant. */
    @Input() activeDarkColor = '';

    /** Total item count shown on the "All" button. */
    @Input() totalCount: number | null = null;

    /** Emits the Set of currently active filter keys whenever it changes. */
    @Output() filtersChanged = new EventEmitter<Set<string>>();

    activeFilters = new Set<string>();

    /** Mobile-only: whether the chip list is expanded. Collapsed by default so the
        bar stays compact on phones; ignored on desktop where chips always show. */
    filtersExpanded = false;

    /** Count of active filter keys — surfaced on the collapse toggle label. */
    get activeCount(): number {
        return this.activeFilters.size;
    }

    toggleFilters(): void {
        this.filtersExpanded = !this.filtersExpanded;
    }

    toggle(key: string): void {
        if (this.activeFilters.has(key)) {
            this.activeFilters.delete(key);
        } else {
            this.activeFilters.add(key);
        }
        this.filtersChanged.emit(new Set(this.activeFilters));
    }

    clearAll(): void {
        this.activeFilters.clear();
        this.filtersChanged.emit(new Set(this.activeFilters));
    }

    /** Host style binding for active color — applied as CSS variable. */
    get activeStyle(): string {
        return `--filter-active-color: ${this.activeColor}; --filter-active-dark-color: ${this.activeDarkColor || this.activeColor}`;
    }
}
